import { useState } from 'react'
import { Avatar, Button, ConfirmButton, Icons, Loading } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useProfile, useRevokeAllSessions, useRevokeSession, useSession, useSessions, useSignOut } from '@/lib/api/hooks'
import { formatRelative, formatTimestamp } from '@/lib/format'
import { personDetails, personName } from '@/lib/people'
import { ProfileForm } from '../../account/ProfileForm'
import { ACCOUNT_STATUS } from '@/features/people/accountStatus'
import { ROLE_LABEL } from '@/features/people/roles'
import { FactsStrip, ProfileGroups } from '@/features/people/profileView'
import { SettingsRow } from '@/shell/Rows'
import { LevelMark } from '@/features/levels/LevelMark'

/**
 * A one-line device name from the user agent — enough to recognize a door
 * at a glance, not a fingerprint to be tracked. An agent nothing here knows
 * answers as its own first word, cut short.
 */
function deviceName(ua: string | null): string {
  if (!ua) return 'Unknown browser'
  const browser = ua.includes('Edg/')
    ? 'Edge'
    : ua.includes('OPR/')
      ? 'Opera'
      : ua.includes('Firefox')
        ? 'Firefox'
        : ua.includes('Chrome')
          ? 'Chrome'
          : ua.includes('Safari')
            ? 'Safari'
            : (ua.split(' ')[0]?.slice(0, 24) || 'Unknown browser')
  const platform = ua.includes('Windows')
    ? 'Windows'
    : ua.includes('Android')
      ? 'Android'
      : ua.includes('iPhone') || ua.includes('iPad')
        ? 'iOS'
        : ua.includes('Mac OS X') || ua.includes('Macintosh')
          ? 'macOS'
          : ua.includes('Linux')
            ? 'Linux'
            : null
  return platform ? `${browser} on ${platform}` : browser
}

/**
 * The signed-in person's own page: who the console assumes, said plainly —
 * the monogram, the name, the address and the role under it, the account's
 * headline facts as a strip, the profile as filed in its groups, the doors
 * their account holds open, and the session's controls last. Nothing here is
 * decoration: this is the person the work is attributed to. The profile is
 * editable in place, as everywhere.
 */
export function AccountPane() {
  const session = useSession()
  const profile = useProfile()
  const sessions = useSessions()
  const signOut = useSignOut()
  const revokeSession = useRevokeSession()
  const revokeAllSessions = useRevokeAllSessions()
  const [editing, setEditing] = useState(false)

  if (session.isLoading || profile.isLoading) return <Loading label="Loading account" />
  const account = session.data?.account
  if (!account) return null

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col items-center gap-1.5 pt-2 text-center">
        <Avatar name={personName(account)} email={account.email} src={account.pictureUrl} size="lg" />
        <p className="font-display text-2xl text-foreground">{personName(account)}</p>
        <p className="flex items-center gap-1.5 text-caption text-muted-foreground">
          {personDetails(account).length > 0 ? <span>{personDetails(account).join(' · ')} ·</span> : null}
          <span>{ROLE_LABEL[account.role]}</span>
          <span aria-hidden>·</span>
          {/* The state as quiet text — a status chip would shout on the own page. */}
          <span>{ACCOUNT_STATUS[account.status].label}</span>
        </p>
        {account.level ? <LevelMark level={account.level} className="mt-1 text-caption text-foreground" /> : null}
      </div>

      <FactsStrip
        facts={[
          { label: 'Member since', value: formatTimestamp(account.createdAt) },
          { label: 'Last signed in', value: account.signedInAt ? formatTimestamp(account.signedInAt) : 'Never' },
        ]}
      />

      <section>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2 className="text-micro text-muted-foreground uppercase">Profile</h2>
          {!editing ? (
            <Button size="xs" variant="ghost" onClick={() => setEditing(true)}>
              Edit
            </Button>
          ) : null}
        </div>
        {editing ? (
          <ProfileForm
            initial={profile.data ?? null}
            submitLabel="Save changes"
            onSubmitted={() => setEditing(false)}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <ProfileGroups profile={profile.data ?? null} />
        )}
      </section>

      <section>
        <h2 className="text-micro mb-1 text-muted-foreground uppercase">Where you're signed in</h2>
        {sessions.data && sessions.data.length > 0 ? (
          sessions.data.map((device) => (
            <SettingsRow
              key={device.id}
              title={deviceName(device.ua)}
              detail={
                `${device.ip ? `IP ${device.ip}` : 'IP address unknown'} · ` +
                (device.lastUsedAt ? `last used ${formatRelative(device.lastUsedAt)}` : 'not used since opening') +
                ` · signed in ${formatTimestamp(device.createdAt)}`
              }
            >
              {device.current ? (
                <span className="text-caption text-muted-foreground">This device</span>
              ) : (
                <ConfirmButton
                  label="Revoke"
                  confirmLabel="Sign it out?"
                  busy={revokeSession.isPending}
                  onConfirm={() => revokeSession.mutate(device.id)}
                />
              )}
            </SettingsRow>
          ))
        ) : sessions.isLoading ? (
          <SettingsRow title="Loading sessions…" detail="Checking where this account is signed in." />
        ) : sessions.error ? (
          <SettingsRow title="Sessions could not be loaded" detail={describeError(sessions.error)} />
        ) : (
          <SettingsRow title="No active sessions" detail="This browser will appear after sign-in." />
        )}
      </section>

      <section>
        <h2 className="text-micro mb-1 text-muted-foreground uppercase">Session</h2>
        <SettingsRow title="Sign out" detail="Ends this session on this browser.">
          <Button size="sm" icon={<Icons.signOut />} busy={signOut.isPending} onClick={() => signOut.mutate(undefined)}>
            Sign out
          </Button>
        </SettingsRow>
        <SettingsRow title="Sign out everywhere" detail="Ends every session of this account, including this one. You land at the sign-in screen.">
          <ConfirmButton
            label="Sign out everywhere"
            ask={{
              title: 'Sign out everywhere?',
              detail: 'Every session of this account ends, including this one.',
              confirmLabel: 'Sign out everywhere',
            }}
            busy={revokeAllSessions.isPending} onConfirm={() => revokeAllSessions.mutate(undefined)} />
        </SettingsRow>
      </section>
    </div>
  )
}
